// WHY-AM interface localization: English, Hindi (Devanagari), Hinglish (Roman).
export const LANGS = [
    { code: "en", label: "English" },
    { code: "hi", label: "हिन्दी" },
    { code: "hing", label: "Hinglish" },
];

const en = {
    "nav.home": "Home", "nav.how": "How it works", "nav.about": "About",
    "nav.pricing": "Pricing", "nav.companion": "AI Companion", "nav.safety": "Safety",
    "nav.contact": "Contact", "nav.assessment": "Assessment", "nav.report": "My Report",
    "nav.profile": "Profile", "nav.admin": "Admin", "nav.login": "Log in",
    "nav.logout": "Log out", "nav.start": "Start Your Journey", "nav.reflections": "Reflections",

    "common.continue": "Continue", "common.back": "Back", "common.loading": "Loading…",
    "common.retry": "Retry", "common.save": "Save", "common.cancel": "Cancel",
    "common.saved": "Saved", "common.saving": "Saving…", "common.skip": "Skip",
    "common.optional": "optional", "common.send": "Send", "common.close": "Close",
    "common.confirm": "Confirm", "common.free": "Free", "common.locked": "Locked",
    "common.required": "This field is required.", "common.yes": "Yes", "common.no": "No",

    "hero.title": "A space for your real thoughts.",
    "hero.sub": "Talk freely. Reflect deeply. Understand yourself better.",
    "hero.tagline": "A space to understand yourself, whoever you are.",

    "ob.welcome.title": "Welcome to WHY-AM",
    "ob.welcome.body": "WHY-AM helps you reflect on situations, emotions, reactions, needs and realistic next steps. It is a space for self-understanding — not a clinical diagnosis, and not a promise of one single answer.",
    "ob.age.title": "First, a quick confirmation",
    "ob.age.body": "WHY-AM is for adults. Please confirm you are 18 or older.",
    "ob.age.confirm": "I confirm I am 18 or older",
    "ob.lang.title": "Choose your language",
    "ob.lang.body": "Your whole experience — questions, report and companion — will use this language. You can change it later without losing your answers.",
    "ob.name.title": "What should we call you?",
    "ob.name.body": "A preferred name is optional. You can skip this.",
    "ob.name.placeholder": "Preferred name (optional)",
    "ob.concern.title": "What feels most present right now?",
    "ob.concern.body": "Pick the area closest to what's on your mind. You can change this later.",
    "ob.consent.title": "Privacy & consent",
    "ob.consent.body": "We collect your answers to generate your preview and report, and to let the AI companion understand your situation. Your data is private to your account. You can request deletion anytime. WHY-AM is a self-reflection tool, not a medical diagnosis.",
    "ob.consent.assessment": "I consent to WHY-AM saving and using my assessment answers to create my report.",
    "ob.consent.terms": "I accept the Terms of Service and Privacy Policy.",
    "ob.consent.memory": "Optionally, allow the AI companion to remember helpful context between sessions (you can turn this off anytime).",
    "ob.account.title": "Create your account",
    "ob.account.body": "This saves your progress so you can return anytime.",
    "ob.email": "Email", "ob.password": "Password (min 6 characters)",
    "ob.create": "Create account & begin",

    "assess.free_tag": "Free reflection",
    "assess.paid_tag": "Full reflection",
    "assess.progress": "Question {n} of {total}",
    "assess.other": "Add anything else (optional)",
    "assess.intensity": "How intense was it? (0–10)",
    "assess.multi_hint": "Choose any that apply.",
    "assess.text_placeholder": "Take your time — a sentence or two is enough.",
    "assess.saveexit": "Save & exit",
    "assess.gen_preview": "See my preview",
    "assess.reassure": "You don't have to be completely sure. Choose what feels closest.",

    "preview.title": "Your preliminary preview",
    "preview.disclaimer": "This short preview is based only on your first four answers. It is a starting point, not a complete analysis.",
    "preview.unlock_title": "Continue the full reflection",
    "preview.unlock_body": "The full reflection explores 14 more questions and creates a personalized report across your situation, triggers, thoughts, reactions, patterns, needs and realistic next steps.",
    "preview.price": "₹299 one-time",
    "preview.cta": "Continue for ₹299",

    "pay.title": "Complete your payment",
    "pay.pending_title": "Payment awaiting verification",
    "pay.pending_body": "Your payment is awaiting verification. Your assessment will unlock after the payment is approved by our team. A transaction reference or screenshot is not proof of payment.",
    "pay.instructions": "Pay ₹299 to the UPI ID below, then enter your transaction reference.",
    "pay.not_configured": "Online payment details are not configured yet. Please use the Contact page and we'll guide you, or check back shortly.",
    "pay.upi_id": "UPI ID",
    "pay.payee": "Payee name",
    "pay.txn_ref": "Transaction reference / UTR number",
    "pay.txn_placeholder": "e.g. 4012XXXXXXXX",
    "pay.submit": "I've paid — submit for verification",
    "pay.status": "Payment status",
    "pay.approved": "Approved — your full reflection is unlocked.",
    "pay.rejected": "We couldn't verify this payment.",
    "pay.go_assessment": "Continue the assessment",
    "pay.amount": "Amount",

    "report.title": "Your reflection report",
    "report.generating": "Creating your personalized report…",
    "report.retry_body": "We couldn't generate your report just now, but your answers are safely saved. You can retry — no extra payment is needed.",
    "report.your_said": "What you shared",
    "report.interpretation": "A possible interpretation",
    "report.open_companion": "Talk it through with your companion",
    "report.sections.your_situation": "Your situation",
    "report.sections.what_you_described": "What you described",
    "report.sections.triggers": "Triggers",
    "report.sections.thoughts_feelings": "Thoughts and feelings",
    "report.sections.reactions": "Reactions",
    "report.sections.recurring_patterns": "Recurring patterns",
    "report.sections.daily_life_impact": "Daily-life impact",
    "report.sections.needs_values": "Your needs and values",
    "report.sections.possible_interpretations": "Possible interpretations",
    "report.sections.what_remains_unknown": "What remains unknown",
    "report.sections.next_steps": "Three realistic next steps",
    "report.sections.reflection_question": "A question to keep exploring",

    "chat.title": "WHY-AM Companion",
    "chat.disclaimer": "I'm an AI companion, not a human therapist. I can't diagnose or predict what others will do — but I'm here to help you think things through.",
    "chat.placeholder": "Share what's on your mind…",
    "chat.new": "New conversation",
    "chat.empty": "Start wherever feels natural. Here are a few ways in:",
    "chat.suggest1": "I keep overthinking after conversations.",
    "chat.suggest2": "Help me understand why this situation affects me.",
    "chat.suggest3": "I'm not sure what I actually need right now.",
    "chat.thinking": "WHY-AM is reflecting…",
    "chat.retry": "Couldn't reply just now. Your message is saved — try again.",

    "profile.title": "Account & privacy",
    "profile.language": "Preferred language",
    "profile.memory": "AI memory",
    "profile.memory_on": "Memory is on. The companion may remember helpful context you share.",
    "profile.memory_off": "Memory is off. The companion won't keep context between sessions.",
    "profile.view_memories": "Saved memories",
    "profile.clear_memories": "Clear all memories",
    "profile.no_memories": "No saved memories yet.",
    "profile.reflections": "My reflections",
    "profile.delete": "Delete my account & data",
    "profile.delete_confirm": "This permanently deletes your account, answers, report and conversations. This cannot be undone.",

    "safety.banner": "If you're in immediate danger or thinking about harming yourself, please reach out to local emergency services or a crisis line right now. You deserve real, human support.",

    "err.generic": "Something went wrong. Please try again.",
    "err.network": "Network issue. Please check your connection and retry.",
    "err.ai_not_configured": "The AI service isn't configured yet. Please contact the owner.",
};

const hi = {
    "nav.home": "होम", "nav.how": "कैसे काम करता है", "nav.about": "परिचय",
    "nav.pricing": "मूल्य", "nav.companion": "AI साथी", "nav.safety": "सुरक्षा",
    "nav.contact": "संपर्क", "nav.assessment": "असेसमेंट", "nav.report": "मेरी रिपोर्ट",
    "nav.profile": "प्रोफ़ाइल", "nav.admin": "एडमिन", "nav.login": "लॉग इन",
    "nav.logout": "लॉग आउट", "nav.start": "अपनी यात्रा शुरू करें", "nav.reflections": "मनन",

    "common.continue": "आगे बढ़ें", "common.back": "पीछे", "common.loading": "लोड हो रहा है…",
    "common.retry": "फिर कोशिश करें", "common.save": "सहेजें", "common.cancel": "रद्द करें",
    "common.saved": "सहेजा गया", "common.saving": "सहेजा जा रहा है…", "common.skip": "छोड़ें",
    "common.optional": "वैकल्पिक", "common.send": "भेजें", "common.close": "बंद करें",
    "common.confirm": "पुष्टि करें", "common.free": "मुफ़्त", "common.locked": "लॉक",
    "common.required": "यह ज़रूरी है।", "common.yes": "हाँ", "common.no": "नहीं",

    "hero.title": "आपके असली विचारों के लिए एक जगह।",
    "hero.sub": "खुलकर बात करें। गहराई से सोचें। खुद को बेहतर समझें।",
    "hero.tagline": "खुद को समझने की एक जगह, आप जो भी हों।",

    "ob.welcome.title": "WHY-AM में आपका स्वागत है",
    "ob.welcome.body": "WHY-AM आपको स्थितियों, भावनाओं, प्रतिक्रियाओं, ज़रूरतों और व्यावहारिक अगले कदमों पर सोचने में मदद करता है। यह खुद को समझने की जगह है — कोई मेडिकल निदान या किसी एक जवाब का वादा नहीं।",
    "ob.age.title": "पहले एक छोटी पुष्टि",
    "ob.age.body": "WHY-AM वयस्कों के लिए है। कृपया पुष्टि करें कि आप 18 या उससे अधिक के हैं।",
    "ob.age.confirm": "मैं पुष्टि करता/करती हूँ कि मैं 18+ हूँ",
    "ob.lang.title": "अपनी भाषा चुनें",
    "ob.lang.body": "आपका पूरा अनुभव — सवाल, रिपोर्ट और साथी — इसी भाषा में होगा। आप इसे बाद में भी बदल सकते हैं, आपके जवाब सुरक्षित रहेंगे।",
    "ob.name.title": "हम आपको क्या कहें?",
    "ob.name.body": "पसंदीदा नाम वैकल्पिक है। आप इसे छोड़ सकते हैं।",
    "ob.name.placeholder": "पसंदीदा नाम (वैकल्पिक)",
    "ob.concern.title": "अभी सबसे ज़्यादा क्या मन में है?",
    "ob.concern.body": "जो क्षेत्र आपके मन के सबसे करीब हो उसे चुनें। इसे बाद में बदला जा सकता है।",
    "ob.consent.title": "गोपनीयता और सहमति",
    "ob.consent.body": "हम आपके जवाब आपकी प्रीव्यू और रिपोर्ट बनाने के लिए सहेजते हैं, और ताकि AI साथी आपकी स्थिति समझ सके। आपका डेटा आपके अकाउंट तक सीमित है। आप कभी भी इसे हटाने का अनुरोध कर सकते हैं। WHY-AM एक आत्म-मनन उपकरण है, मेडिकल निदान नहीं।",
    "ob.consent.assessment": "मैं सहमति देता/देती हूँ कि WHY-AM मेरी रिपोर्ट बनाने के लिए मेरे जवाब सहेजे और उपयोग करे।",
    "ob.consent.terms": "मैं नियम और गोपनीयता नीति स्वीकार करता/करती हूँ।",
    "ob.consent.memory": "वैकल्पिक रूप से, AI साथी को सत्रों के बीच उपयोगी संदर्भ याद रखने दें (आप इसे कभी भी बंद कर सकते हैं)।",
    "ob.account.title": "अपना अकाउंट बनाएँ",
    "ob.account.body": "यह आपकी प्रगति सहेजता है ताकि आप कभी भी लौट सकें।",
    "ob.email": "ईमेल", "ob.password": "पासवर्ड (कम से कम 6 अक्षर)",
    "ob.create": "अकाउंट बनाएँ और शुरू करें",

    "assess.free_tag": "मुफ़्त मनन",
    "assess.paid_tag": "पूरा मनन",
    "assess.progress": "प्रश्न {n} / {total}",
    "assess.other": "कुछ और जोड़ें (वैकल्पिक)",
    "assess.intensity": "यह कितना तीव्र था? (0–10)",
    "assess.multi_hint": "जो भी लागू हो चुनें।",
    "assess.text_placeholder": "आराम से — एक-दो वाक्य काफी हैं।",
    "assess.saveexit": "सहेजें और बाहर निकलें",
    "assess.gen_preview": "मेरा प्रीव्यू देखें",
    "assess.reassure": "आपको पूरी तरह निश्चित होने की ज़रूरत नहीं है। जो सबसे करीब लगे उसे चुनें।",

    "preview.title": "आपका प्रारंभिक प्रीव्यू",
    "preview.disclaimer": "यह छोटा प्रीव्यू केवल आपके पहले चार जवाबों पर आधारित है। यह एक शुरुआत है, पूरा विश्लेषण नहीं।",
    "preview.unlock_title": "पूरा मनन जारी रखें",
    "preview.unlock_body": "पूरा मनन 14 और सवालों की खोज करता है और आपकी स्थिति, ट्रिगर, विचार, प्रतिक्रियाओं, पैटर्न, ज़रूरतों और व्यावहारिक अगले कदमों पर एक व्यक्तिगत रिपोर्ट बनाता है।",
    "preview.price": "₹299 एक बार",
    "preview.cta": "₹299 में जारी रखें",

    "pay.title": "अपना भुगतान पूरा करें",
    "pay.pending_title": "भुगतान सत्यापन की प्रतीक्षा में",
    "pay.pending_body": "आपका भुगतान सत्यापन की प्रतीक्षा में है। मंज़ूरी के बाद आपका असेसमेंट अनलॉक होगा। ट्रांज़ैक्शन रेफरेंस या स्क्रीनशॉट भुगतान का प्रमाण नहीं है।",
    "pay.instructions": "नीचे दिए UPI ID पर ₹299 भेजें, फिर अपना ट्रांज़ैक्शन रेफरेंस डालें।",
    "pay.not_configured": "ऑनलाइन भुगतान विवरण अभी सेट नहीं हैं। कृपया संपर्क पेज का उपयोग करें, हम मार्गदर्शन करेंगे।",
    "pay.upi_id": "UPI ID",
    "pay.payee": "प्राप्तकर्ता का नाम",
    "pay.txn_ref": "ट्रांज़ैक्शन रेफरेंस / UTR नंबर",
    "pay.txn_placeholder": "जैसे 4012XXXXXXXX",
    "pay.submit": "मैंने भुगतान किया — सत्यापन के लिए भेजें",
    "pay.status": "भुगतान स्थिति",
    "pay.approved": "मंज़ूर — आपका पूरा मनन अनलॉक है।",
    "pay.rejected": "हम इस भुगतान की पुष्टि नहीं कर सके।",
    "pay.go_assessment": "असेसमेंट जारी रखें",
    "pay.amount": "राशि",

    "report.title": "आपकी मनन रिपोर्ट",
    "report.generating": "आपकी व्यक्तिगत रिपोर्ट बन रही है…",
    "report.retry_body": "हम अभी आपकी रिपोर्ट नहीं बना सके, पर आपके जवाब सुरक्षित हैं। आप फिर कोशिश कर सकते हैं — कोई अतिरिक्त भुगतान नहीं चाहिए।",
    "report.your_said": "आपने जो साझा किया",
    "report.interpretation": "एक संभावित व्याख्या",
    "report.open_companion": "अपने साथी के साथ इस पर बात करें",
    "report.sections.your_situation": "आपकी स्थिति",
    "report.sections.what_you_described": "आपने जो बताया",
    "report.sections.triggers": "ट्रिगर",
    "report.sections.thoughts_feelings": "विचार और भावनाएँ",
    "report.sections.reactions": "प्रतिक्रियाएँ",
    "report.sections.recurring_patterns": "बार-बार होने वाले पैटर्न",
    "report.sections.daily_life_impact": "रोज़मर्रा पर असर",
    "report.sections.needs_values": "आपकी ज़रूरतें और मूल्य",
    "report.sections.possible_interpretations": "संभावित व्याख्याएँ",
    "report.sections.what_remains_unknown": "जो अभी अज्ञात है",
    "report.sections.next_steps": "तीन व्यावहारिक अगले कदम",
    "report.sections.reflection_question": "सोचते रहने के लिए एक सवाल",

    "chat.title": "WHY-AM साथी",
    "chat.disclaimer": "मैं एक AI साथी हूँ, कोई इंसानी थेरेपिस्ट नहीं। मैं निदान या दूसरों के व्यवहार की भविष्यवाणी नहीं कर सकता — पर सोचने में आपकी मदद कर सकता हूँ।",
    "chat.placeholder": "जो मन में है, साझा करें…",
    "chat.new": "नई बातचीत",
    "chat.empty": "जहाँ से स्वाभाविक लगे वहीं से शुरू करें। कुछ तरीके:",
    "chat.suggest1": "बातचीत के बाद मैं बहुत सोचता/सोचती रहता/रहती हूँ।",
    "chat.suggest2": "मुझे समझने में मदद करें कि यह स्थिति मुझे क्यों प्रभावित करती है।",
    "chat.suggest3": "मुझे नहीं पता अभी मुझे असल में क्या चाहिए।",
    "chat.thinking": "WHY-AM सोच रहा है…",
    "chat.retry": "अभी जवाब नहीं दे सका। आपका संदेश सुरक्षित है — फिर कोशिश करें।",

    "profile.title": "अकाउंट और गोपनीयता",
    "profile.language": "पसंदीदा भाषा",
    "profile.memory": "AI मेमोरी",
    "profile.memory_on": "मेमोरी चालू है। साथी आपके द्वारा साझा उपयोगी संदर्भ याद रख सकता है।",
    "profile.memory_off": "मेमोरी बंद है। साथी सत्रों के बीच संदर्भ नहीं रखेगा।",
    "profile.view_memories": "सहेजी गई मेमोरी",
    "profile.clear_memories": "सारी मेमोरी हटाएँ",
    "profile.no_memories": "अभी कोई मेमोरी सहेजी नहीं गई।",
    "profile.reflections": "मेरे मनन",
    "profile.delete": "मेरा अकाउंट और डेटा हटाएँ",
    "profile.delete_confirm": "यह आपका अकाउंट, जवाब, रिपोर्ट और बातचीत स्थायी रूप से हटा देता है। इसे वापस नहीं लाया जा सकता।",

    "safety.banner": "यदि आप तत्काल खतरे में हैं या खुद को नुकसान पहुँचाने की सोच रहे हैं, तो कृपया अभी स्थानीय आपातकालीन सेवाओं या क्राइसिस लाइन से संपर्क करें। आप असली, इंसानी सहारे के हक़दार हैं।",

    "err.generic": "कुछ गड़बड़ हुई। कृपया फिर कोशिश करें।",
    "err.network": "नेटवर्क समस्या। कनेक्शन जाँचें और फिर कोशिश करें।",
    "err.ai_not_configured": "AI सेवा अभी सेट नहीं है। कृपया मालिक से संपर्क करें।",
};

const hing = {
    "nav.home": "Home", "nav.how": "Kaise kaam karta hai", "nav.about": "About",
    "nav.pricing": "Pricing", "nav.companion": "AI Saathi", "nav.safety": "Safety",
    "nav.contact": "Contact", "nav.assessment": "Assessment", "nav.report": "Meri Report",
    "nav.profile": "Profile", "nav.admin": "Admin", "nav.login": "Log in",
    "nav.logout": "Log out", "nav.start": "Apni journey shuru karein", "nav.reflections": "Reflections",

    "common.continue": "Aage badhein", "common.back": "Peeche", "common.loading": "Load ho raha hai…",
    "common.retry": "Phir koshish karein", "common.save": "Save", "common.cancel": "Cancel",
    "common.saved": "Save ho gaya", "common.saving": "Save ho raha hai…", "common.skip": "Skip",
    "common.optional": "optional", "common.send": "Bhejein", "common.close": "Close",
    "common.confirm": "Confirm", "common.free": "Free", "common.locked": "Locked",
    "common.required": "Ye zaroori hai.", "common.yes": "Haan", "common.no": "Nahi",

    "hero.title": "Aapke real thoughts ke liye ek jagah.",
    "hero.sub": "Khulkar baat karein. Gehrai se sochein. Khud ko better samjhein.",
    "hero.tagline": "Khud ko samajhne ki ek jagah, aap jo bhi hon.",

    "ob.welcome.title": "WHY-AM mein aapka swagat hai",
    "ob.welcome.body": "WHY-AM aapko situations, emotions, reactions, needs aur realistic next steps par sochne mein help karta hai. Ye khud ko samajhne ki jagah hai — koi medical diagnosis ya ek hi jawab ka promise nahi.",
    "ob.age.title": "Pehle ek chhoti confirmation",
    "ob.age.body": "WHY-AM adults ke liye hai. Kripya confirm karein ki aap 18 ya usse zyada ke hain.",
    "ob.age.confirm": "Main confirm karta/karti hoon ki main 18+ hoon",
    "ob.lang.title": "Apni language choose karein",
    "ob.lang.body": "Aapka poora experience — questions, report aur saathi — isi language mein hoga. Aap ise baad mein bhi badal sakte hain, jawab safe rahenge.",
    "ob.name.title": "Hum aapko kya bulayein?",
    "ob.name.body": "Preferred naam optional hai. Aap ise skip kar sakte hain.",
    "ob.name.placeholder": "Preferred naam (optional)",
    "ob.concern.title": "Abhi sabse zyada kya mann mein hai?",
    "ob.concern.body": "Jo area aapke mann ke sabse close ho use choose karein. Ise baad mein badal sakte hain.",
    "ob.consent.title": "Privacy & consent",
    "ob.consent.body": "Hum aapke jawab aapki preview aur report banane ke liye save karte hain, aur taaki AI saathi aapki situation samajh sake. Aapka data aapke account tak seemit hai. Aap kabhi bhi delete request kar sakte hain. WHY-AM ek self-reflection tool hai, medical diagnosis nahi.",
    "ob.consent.assessment": "Main consent deta/deti hoon ki WHY-AM meri report banane ke liye mere jawab save aur use kare.",
    "ob.consent.terms": "Main Terms aur Privacy Policy accept karta/karti hoon.",
    "ob.consent.memory": "Optionally, AI saathi ko sessions ke beech helpful context yaad rakhne dein (kabhi bhi band kar sakte hain).",
    "ob.account.title": "Apna account banayein",
    "ob.account.body": "Ye aapki progress save karta hai taaki aap kabhi bhi wapas aa sakein.",
    "ob.email": "Email", "ob.password": "Password (kam se kam 6 characters)",
    "ob.create": "Account banayein aur shuru karein",

    "assess.free_tag": "Free reflection",
    "assess.paid_tag": "Poora reflection",
    "assess.progress": "Question {n} / {total}",
    "assess.other": "Kuch aur add karein (optional)",
    "assess.intensity": "Ye kitna intense tha? (0–10)",
    "assess.multi_hint": "Jo bhi apply ho choose karein.",
    "assess.text_placeholder": "Aaram se — ek do line kaafi hai.",
    "assess.saveexit": "Save & exit",
    "assess.gen_preview": "Mera preview dekhein",
    "assess.reassure": "Aapko bilkul sure hone ki zaroorat nahi hai. Jo sabse close lage use choose karein.",

    "preview.title": "Aapka preliminary preview",
    "preview.disclaimer": "Ye chhota preview sirf aapke pehle chaar jawabon par based hai. Ye ek shuruaat hai, poora analysis nahi.",
    "preview.unlock_title": "Poora reflection continue karein",
    "preview.unlock_body": "Poora reflection 14 aur questions explore karta hai aur aapki situation, triggers, thoughts, reactions, patterns, needs aur realistic next steps par ek personalized report banata hai.",
    "preview.price": "₹299 one-time",
    "preview.cta": "₹299 mein continue karein",

    "pay.title": "Apna payment complete karein",
    "pay.pending_title": "Payment verification ka wait",
    "pay.pending_body": "Aapka payment verification ka wait kar raha hai. Approval ke baad aapka assessment unlock hoga. Transaction reference ya screenshot payment ka proof nahi hai.",
    "pay.instructions": "Neeche diye UPI ID par ₹299 bhejein, phir apna transaction reference daalein.",
    "pay.not_configured": "Online payment details abhi configure nahi hain. Kripya Contact page use karein, hum guide karenge.",
    "pay.upi_id": "UPI ID",
    "pay.payee": "Payee naam",
    "pay.txn_ref": "Transaction reference / UTR number",
    "pay.txn_placeholder": "jaise 4012XXXXXXXX",
    "pay.submit": "Maine pay kiya — verification ke liye bhejein",
    "pay.status": "Payment status",
    "pay.approved": "Approved — aapka poora reflection unlock hai.",
    "pay.rejected": "Hum is payment ko verify nahi kar paaye.",
    "pay.go_assessment": "Assessment continue karein",
    "pay.amount": "Amount",

    "report.title": "Aapki reflection report",
    "report.generating": "Aapki personalized report ban rahi hai…",
    "report.retry_body": "Hum abhi aapki report nahi bana paaye, par aapke jawab safe hain. Aap retry kar sakte hain — koi extra payment ki zaroorat nahi.",
    "report.your_said": "Aapne jo share kiya",
    "report.interpretation": "Ek possible interpretation",
    "report.open_companion": "Apne saathi ke saath is par baat karein",
    "report.sections.your_situation": "Aapki situation",
    "report.sections.what_you_described": "Aapne jo bataya",
    "report.sections.triggers": "Triggers",
    "report.sections.thoughts_feelings": "Thoughts aur feelings",
    "report.sections.reactions": "Reactions",
    "report.sections.recurring_patterns": "Recurring patterns",
    "report.sections.daily_life_impact": "Daily life par asar",
    "report.sections.needs_values": "Aapki needs aur values",
    "report.sections.possible_interpretations": "Possible interpretations",
    "report.sections.what_remains_unknown": "Jo abhi unknown hai",
    "report.sections.next_steps": "Teen realistic next steps",
    "report.sections.reflection_question": "Sochte rehne ke liye ek sawaal",

    "chat.title": "WHY-AM Saathi",
    "chat.disclaimer": "Main ek AI saathi hoon, koi human therapist nahi. Main diagnose ya doosron ke behaviour ki prediction nahi kar sakta — par sochne mein aapki help kar sakta hoon.",
    "chat.placeholder": "Jo mann mein hai share karein…",
    "chat.new": "Nayi baat-cheet",
    "chat.empty": "Jahan se natural lage wahin se shuru karein. Kuch tareeke:",
    "chat.suggest1": "Conversations ke baad main overthink karta/karti rehta/rehti hoon.",
    "chat.suggest2": "Samajhne mein help karein ki ye situation mujhe kyun affect karti hai.",
    "chat.suggest3": "Mujhe nahi pata abhi mujhe actually kya chahiye.",
    "chat.thinking": "WHY-AM soch raha hai…",
    "chat.retry": "Abhi reply nahi de paaya. Aapka message safe hai — phir koshish karein.",

    "profile.title": "Account & privacy",
    "profile.language": "Preferred language",
    "profile.memory": "AI memory",
    "profile.memory_on": "Memory on hai. Saathi aapke share kiye helpful context ko yaad rakh sakta hai.",
    "profile.memory_off": "Memory off hai. Saathi sessions ke beech context nahi rakhega.",
    "profile.view_memories": "Saved memories",
    "profile.clear_memories": "Saari memories clear karein",
    "profile.no_memories": "Abhi koi memory save nahi hui.",
    "profile.reflections": "Mere reflections",
    "profile.delete": "Mera account aur data delete karein",
    "profile.delete_confirm": "Ye aapka account, jawab, report aur conversations permanently delete kar deta hai. Ise wapas nahi laaya ja sakta.",

    "safety.banner": "Agar aap immediate danger mein hain ya khud ko nuksan pahunchane ke baare mein soch rahe hain, to kripya abhi local emergency services ya crisis line se contact karein. Aap real, human support ke haqdaar hain.",

    "err.generic": "Kuch gadbad hui. Kripya phir koshish karein.",
    "err.network": "Network issue. Connection check karke retry karein.",
    "err.ai_not_configured": "AI service abhi configure nahi hai. Kripya owner se contact karein.",
};

export const dict = { en, hi, hing };

export function translate(lang, key, vars) {
    const table = dict[lang] || en;
    let s = table[key] != null ? table[key] : (en[key] != null ? en[key] : key);
    if (vars) Object.keys(vars).forEach((k) => { s = s.replace(`{${k}}`, vars[k]); });
    return s;
}
